import * as Yup from 'yup';
import { useState } from 'react';

// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Alert, Box, Button, Divider, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFCheckbox, RHFTextField } from '../../../components/hook-form';
import { BUSINESS_NAME } from '../../../config';
import { toast } from 'react-toastify';

// ----------------------------------------------------------------------

const BRAND = {
  pink: '#EC1467',
  brown: '#43291A',
  ink: '#14151A',
  muted: '#8A8F98',
  line: '#E7E7EC',
};

const fieldStyle = {
  '& .MuiInputLabel-root': {
    color: BRAND.muted,
    fontSize: 15,
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: BRAND.pink,
  },
  '& .MuiInput-root': {
    color: BRAND.ink,
    fontSize: 15,
  },
  '& .MuiInput-root:before': {
    borderBottom: `1.5px solid ${BRAND.line}`,
  },
  '& .MuiInput-root:hover:not(.Mui-disabled):before': {
    borderBottom: `1.5px solid ${BRAND.ink}`,
  },
  '& .MuiInput-root:after': {
    borderBottomColor: BRAND.pink,
  },
  '& .MuiFormHelperText-root': {
    mx: 0,
  },
};

// ----------------------------------------------------------------------

export default function LoginForm() {
  const { login, register, signInWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const defaultValues = {
    email: '',
    password: '',
    remember: true,
  };

  const methods = useForm({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    formState: { errors },
  } = methods;

  const handleSignInWithPopup = async () => {
    setIsLoading(true);
    const response = await signInWithGoogle();
    if (response.status) {
    } else {
      setError('afterSubmit', { message: response.code });
    }
    setIsLoading(false);
  };

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await login(data.email, data.password).then((loginStatus) => {
        if (loginStatus?.status) {
        } else {
          setError('afterSubmit', { message: loginStatus?.code });
        }
      });
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        await register(data.email, data.password).then((registerStatus) => {
          if (registerStatus?.status) {
          } else {
            setError('afterSubmit', { message: registerStatus?.code });
          }
        });
      } else {
        reset();
        if (isMountedRef.current) {
          setError('afterSubmit', { ...error, message: error.message });
        }
      }
    }
    setIsLoading(false);
  };

  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={3.25}>
          {!!errors.afterSubmit && (
            <Alert severity='error' sx={{ borderRadius: 1.5 }}>
              {errors.afterSubmit.message}
            </Alert>
          )}

          <RHFTextField name='email' label='Email address' type='email' variant='standard' capitalize={false} autoComplete='email' sx={fieldStyle} />

          <RHFTextField
            name='password'
            label='Password'
            type={showPassword ? 'text' : 'password'}
            variant='standard'
            capitalize={false}
            autoComplete='current-password'
            sx={fieldStyle}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    onClick={() => setShowPassword(!showPassword)}
                    edge='end'
                    sx={{ color: BRAND.muted }}
                  >
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} width={20} height={20} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>

        <Stack direction='row' alignItems='center' justifyContent='space-between' spacing={1} sx={{ my: 2.5 }}>
          <RHFCheckbox
            name='remember'
            label='Remember me'
            sx={{
              m: 0,
              color: BRAND.ink,
              '& .MuiCheckbox-root': {
                p: 0.75,
                mr: 0.25,
                color: BRAND.line,
                '&.Mui-checked': { color: BRAND.pink },
              },
              '& .MuiFormControlLabel-label': { fontSize: 13.5 },
            }}
          />
          <Button
            className='pointer'
            onClick={() => toast.info(`Please contact the ${BUSINESS_NAME} team.`)}
            sx={{ minWidth: 0, p: 0.5, color: BRAND.pink, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { backgroundColor: 'rgba(236, 20, 103, 0.06)' } }}
          >
            Forgot password?
          </Button>
        </Stack>

        <LoadingButton
          fullWidth
          size='large'
          type='submit'
          variant='contained'
          loading={isLoading}
          endIcon={<Iconify icon='eva:diagonal-arrow-right-up-fill' width={18} height={18} />}
          sx={{
            minHeight: 52,
            borderRadius: 1.5,
            backgroundColor: BRAND.brown,
            boxShadow: 'none',
            fontSize: 14.5,
            '&:hover': { backgroundColor: BRAND.pink, boxShadow: 'none' },
            '&:focus-visible': { outline: `3px solid rgba(236, 20, 103, 0.28)`, outlineOffset: 2 },
          }}
        >
          Login / Register
        </LoadingButton>
      </FormProvider>

      <Stack direction='row' alignItems='center' spacing={1.5} sx={{ my: 2.5 }} aria-hidden='true'>
        <Divider sx={{ flex: 1, borderColor: BRAND.line }} />
        <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>or</Typography>
        <Divider sx={{ flex: 1, borderColor: BRAND.line }} />
      </Stack>

      <LoadingButton
        fullWidth
        size='large'
        variant='outlined'
        loading={isLoading}
        onClick={handleSignInWithPopup}
        startIcon={<Iconify icon='mdi:google' width={19} height={19} />}
        sx={{
          minHeight: 50,
          borderRadius: 1.5,
          borderWidth: 1.5,
          borderColor: BRAND.line,
          color: BRAND.ink,
          fontSize: 14,
          fontWeight: 600,
          '& .MuiLoadingButton-startIcon': { color: '#EA4335' },
          '&:hover': { borderWidth: 1.5, borderColor: BRAND.ink, backgroundColor: '#FFFFFF' },
          '&:focus-visible': { outline: `3px solid rgba(236, 20, 103, 0.22)`, outlineOffset: 2 },
        }}
      >
        Sign in With Google
      </LoadingButton>
    </>
  );
}

// ----------------------------------------------------------------------
// LEGACY LOGIN FORM (pre-redesign)
// Rollback: comment out the active implementation above, then uncomment
// every line in this legacy block.
// ----------------------------------------------------------------------
// import * as Yup from 'yup';
// import { useState } from 'react';
//
// // form
// import { useForm } from 'react-hook-form';
// import { yupResolver } from '@hookform/resolvers/yup';
// // @mui
// import { Alert, Button, IconButton, InputAdornment, Stack } from '@mui/material';
// import { LoadingButton } from '@mui/lab';
//
// // hooks
// import useAuth from '../../../hooks/useAuth';
// import useIsMountedRef from '../../../hooks/useIsMountedRef';
// // components
// import Iconify from '../../../components/Iconify';
// import { FormProvider, RHFCheckbox, RHFTextField } from '../../../components/hook-form';
// import { BUSINESS_NAME } from '../../../config';
// import { toast } from 'react-toastify';
//
// // ----------------------------------------------------------------------
//
// export default function LoginForm() {
//   const { login, register, signInWithGoogle } = useAuth();
//   const [isLoading, setIsLoading] = useState(false);
//
//   const isMountedRef = useIsMountedRef();
//
//   const [showPassword, setShowPassword] = useState(false);
//
//   const LoginSchema = Yup.object().shape({
//     email: Yup.string().email('Email must be a valid email address').required('Email is required'),
//     password: Yup.string().required('Password is required'),
//   });
//
//   const defaultValues = {
//     email: '',
//     password: '',
//     remember: true,
//   };
//
//   const methods = useForm({
//     resolver: yupResolver(LoginSchema),
//     defaultValues,
//   });
//
//   const {
//     reset,
//     setError,
//     handleSubmit,
//     formState: { errors },
//   } = methods;
//
//   const handleSignInWithPopup = async () => {
//     setIsLoading(true);
//     const response = await signInWithGoogle();
//     if (response.status) {
//     } else {
//       setError('afterSubmit', { message: response.code });
//     }
//     setIsLoading(false);
//   };
//
//   const onSubmit = async (data) => {
//     setIsLoading(true);
//     try {
//       await login(data.email, data.password).then((loginStatus) => {
//         if (loginStatus?.status) {
//         } else {
//           setError('afterSubmit', { message: loginStatus?.code });
//         }
//       });
//     } catch (error) {
//       if (error.code === 'auth/email-already-in-use') {
//         await register(data.email, data.password).then((registerStatus) => {
//           if (registerStatus?.status) {
//           } else {
//             setError('afterSubmit', { message: registerStatus?.code });
//           }
//         });
//       } else {
//         reset();
//         if (isMountedRef.current) {
//           setError('afterSubmit', { ...error, message: error.message });
//         }
//       }
//     }
//     setIsLoading(false);
//   };
//
//   return (
//     <>
//       <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
//         <Stack spacing={3}>
//           {!!errors.afterSubmit && <Alert severity='error'>{errors.afterSubmit.message}</Alert>}
//
//           <RHFTextField name='email' label='Email address' capitalize={false} />
//
//           <RHFTextField
//             name='password'
//             label='Password'
//             type={showPassword ? 'text' : 'password'}
//             capitalize={false}
//             InputProps={{
//               endAdornment: (
//                 <InputAdornment position='end'>
//                   <IconButton onClick={() => setShowPassword(!showPassword)} edge='end'>
//                     <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
//                   </IconButton>
//                 </InputAdornment>
//               ),
//             }}
//           />
//         </Stack>
//
//         <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ my: 2 }}>
//           <RHFCheckbox name='remember' label='Remember me' />
//           <Button color={'primary'} className={'pointer'} onClick={() => toast.info(`Please contact the ${BUSINESS_NAME} team.`)}>
//             Forgot password?
//           </Button>
//         </Stack>
//
//         <LoadingButton fullWidth size='large' type='submit' variant='contained' loading={isLoading}>
//           Login / Register
//         </LoadingButton>
//       </FormProvider>
//       <br />
//       <Stack spacing={1}>
//         <LoadingButton fullWidth size='large' variant='outlined' loading={isLoading} onClick={handleSignInWithPopup} color={'error'} startIcon={<Iconify icon={'mdi:google'} />}>
//           Sign in With Google
//         </LoadingButton>
//       </Stack>
//     </>
//   );
// }
